// src/pages/usuarios/Usuarios.js — usuarios y contraseñas en PostgreSQL (vía API)
import React, { useState, useEffect, useMemo } from 'react';
import Swal from 'sweetalert2';
import { useMe } from '../../hooks/useMe';
import {
  listUsuarios,
  createUsuario,
  updateUsuario,
  deleteUsuario,
  listSucursales,
} from '../../services/data';
import './Usuarios.css';

const MIN_PASSWORD = 8;

export default function Usuarios() {
  const me = useMe();
  const [usuarios, setUsuarios] = useState([]);
  const [sucursales, setSucursales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [permError, setPermError] = useState('');

  const sucMap = useMemo(
    () => Object.fromEntries(sucursales.map(s => [s.id, s.nombre])),
    [sucursales]
  );

  // Form registro
  const [username, setUsername] = useState('');
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [showRegPwd, setShowRegPwd] = useState(false);
  const [role, setRole]         = useState('viewer');
  const [sucursalId, setSucursalId] = useState(''); // requerido si role === 'viewer'

  // UI state
  const [selectedId, setSelectedId] = useState(null);
  const [working, setWorking] = useState(false);

  const isEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v||'').trim());

  const cargarUsuarios = async () => {
    const rows = await listUsuarios();
    setUsuarios(rows.map(r => ({ id: r.uid, ...r })));
  };

  // ========= Cargas =========
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setPermError('');
        try {
          await cargarUsuarios();
        } catch (e) {
          console.error('Error al cargar usuarios:', e);
          setPermError(e?.message || 'Error al cargar usuarios');
          Swal.fire('Error', 'No se pudieron cargar los usuarios', 'error');
        }
        try {
          setSucursales(await listSucursales());
        } catch (e) {
          console.error('Error al cargar sucursales:', e);
          setPermError(prev => prev || e?.message || '');
          Swal.fire('Error', 'No se pudieron cargar las sucursales', 'error');
        }
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // ========= Registrar =========
  const handleRegister = async (e) => {
    e.preventDefault();

    if (!username.trim() || !email.trim() || !password.trim()) {
      return Swal.fire('Advertencia', 'Completa todos los campos', 'warning');
    }
    if (!isEmail(email)) {
      return Swal.fire('Advertencia', 'Email inválido', 'warning');
    }
    if (password.length < MIN_PASSWORD) {
      return Swal.fire('Advertencia', `La contraseña debe tener al menos ${MIN_PASSWORD} caracteres`, 'warning');
    }
    if (usuarios.some(u => String(u.email).toLowerCase() === String(email).toLowerCase())) {
      return Swal.fire('Advertencia', 'Ese email ya está registrado', 'warning');
    }
    if (role === 'viewer' && !sucursalId) {
      return Swal.fire('Advertencia', 'Selecciona una sucursal para el usuario viewer', 'warning');
    }

    try {
      setWorking(true);
      await createUsuario({
        username: username.trim(),
        email: email.trim(),
        password,
        role,
        sucursalId: role === 'viewer' ? sucursalId : null,
      });

      Swal.fire('Éxito', 'Usuario registrado', 'success');
      setUsername(''); setEmail(''); setPassword(''); setShowRegPwd(false);
      setRole('viewer'); setSucursalId('');
      await cargarUsuarios();
    } catch (err) {
      console.error(err);
      Swal.fire('Error', err.message || 'No se pudo registrar', 'error');
    } finally {
      setWorking(false);
    }
  };

  // ========= Selección =========
  const handleSelect = (id) => setSelectedId(id === selectedId ? null : id);

  // ========= Activar/Desactivar =========
  const handleToggleDisabled = async () => {
    if (!selectedId) return;
    const u = usuarios.find(x => x.id === selectedId);
    if (!u) return;
    try {
      setWorking(true);
      const next = !u.disabled;
      await updateUsuario({ uid: u.id, disabled: next });
      Swal.fire('OK', next ? 'Usuario deshabilitado' : 'Usuario activado', 'success');
      await cargarUsuarios();
    } catch (e) {
      console.error(e);
      Swal.fire('Error', e?.message || 'No se pudo cambiar el estado', 'error');
    } finally {
      setWorking(false);
    }
  };

  // ========= Eliminar =========
  const handleDelete = async () => {
    if (!selectedId) return;
    if (selectedId === me.uid) {
      Swal.fire('No permitido', 'No puedes eliminar tu propia cuenta', 'info');
      return;
    }
    const confirm = await Swal.fire({
      title: '¿Eliminar usuario?',
      text: 'Esta acción no se puede deshacer.',
      icon: 'warning', showCancelButton: true, confirmButtonText: 'Sí'
    });
    if (!confirm.isConfirmed) return;

    try {
      setWorking(true);
      await deleteUsuario(selectedId);
      Swal.fire('Eliminado', 'Usuario eliminado', 'success');
      setSelectedId(null);
      await cargarUsuarios();
    } catch (err) {
      console.warn('Eliminar usuario falló:', err);
      Swal.fire('Error', err.message || 'No se pudo eliminar', 'error');
    } finally {
      setWorking(false);
    }
  };

  // ========= Editar (username, rol, sucursal, email y contraseña) =========
  const handleEdit = async () => {
    if (!selectedId) return;
    const u = usuarios.find(x => x.id === selectedId);
    if (!u) return;

    // 1) Username
    const rUser = await Swal.fire({
      title: 'Editar usuario',
      input: 'text',
      inputLabel: 'Nombre de usuario',
      inputValue: u.username || '',
      showCancelButton: true,
      confirmButtonText: 'Siguiente',
      inputValidator: (v) => (!v || !v.trim() ? 'Requerido' : undefined)
    });
    if (!rUser.isConfirmed) return;
    const newUsername = rUser.value.trim();

    // 2) Rol
    const rRole = await Swal.fire({
      title: 'Rol',
      input: 'select',
      inputOptions: {
        admin: 'Admin (todas las sucursales)',
        viewer: 'Viewer (solo ver, requiere sucursal)'
      },
      inputValue: u.role || 'viewer',
      showCancelButton: true,
      confirmButtonText: 'Siguiente'
    });
    if (!rRole.isConfirmed) return;
    const newRole = rRole.value;

    // 3) Sucursal si viewer
    let newSucursalId = u.sucursalId || '';
    if (newRole === 'viewer') {
      const inputOptions = Object.fromEntries(
        sucursales.map(s => [s.id, s.nombre + (s.ubicacion ? ` – ${s.ubicacion}` : '')])
      );
      const rSuc = await Swal.fire({
        title: 'Sucursal asignada',
        input: 'select',
        inputOptions,
        inputValue: newSucursalId || '',
        inputPlaceholder: 'Selecciona sucursal',
        showCancelButton: true,
        confirmButtonText: 'Siguiente'
      });
      if (!rSuc.isConfirmed) return;
      newSucursalId = rSuc.value;
      if (!newSucursalId) {
        Swal.fire('Advertencia', 'Debes seleccionar una sucursal para viewer', 'warning');
        return;
      }
    } else {
      newSucursalId = null; // admin => todas
    }

    // 4) ¿Cambiar email?
    let newEmail = u.email || '';
    const changeEmail = await Swal.fire({
      title: '¿Cambiar email?',
      text: `Email actual: ${u.email || '—'}`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sí, cambiar',
      cancelButtonText: 'No'
    });
    if (changeEmail.isConfirmed) {
      const rEmail = await Swal.fire({
        title: 'Nuevo email',
        input: 'email',
        inputValue: u.email || '',
        inputValidator: (v) => (!isEmail(v) ? 'Email inválido' : undefined),
        showCancelButton: true,
        confirmButtonText: 'Guardar email'
      });
      if (!rEmail.isConfirmed) return;
      newEmail = rEmail.value.trim();
    }

    // 5) ¿Cambiar contraseña? (la define el admin; no se envía correo)
    let newPassword = '';
    const changePwd = await Swal.fire({
      title: 'Contraseña',
      text: '¿Deseas establecer una nueva contraseña para este usuario?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sí, establecer',
      cancelButtonText: 'No'
    });
    if (changePwd.isConfirmed) {
      const rTemp = await Swal.fire({
        title: 'Nueva contraseña',
        input: 'text',
        inputPlaceholder: `Mínimo ${MIN_PASSWORD} caracteres`,
        inputValidator: (v) => (!v || v.length < MIN_PASSWORD ? `Mínimo ${MIN_PASSWORD} caracteres` : undefined),
        showCancelButton: true,
        confirmButtonText: 'Establecer'
      });
      if (!rTemp.isConfirmed) return;
      newPassword = rTemp.value;
    }

    try {
      setWorking(true);
      await updateUsuario({
        uid: u.id,
        username: newUsername,
        role: newRole,
        sucursalId: newRole === 'viewer' ? newSucursalId : null,
        email: (newEmail && newEmail !== u.email) ? newEmail : undefined,
        password: newPassword || undefined,
      });

      Swal.fire('Actualizado', 'Usuario modificado', 'success');
      setSelectedId(null);
      await cargarUsuarios();
    } catch (err) {
      console.error(err);
      Swal.fire('Error', err.message || 'No se pudo actualizar', 'error');
    } finally {
      setWorking(false);
    }
  };

  return (
    <div className="usuarios-shell">
      <header className="usuarios-header">
        <h1>Usuarios</h1>
      </header>

      <div className="usuarios-panels">
        {/* Panel Registrar */}
        <section className="card">
          <h3 className="card-title">Registrar usuario</h3>
          <form onSubmit={handleRegister} className="form-usuario">
            <div className="form-row">
              <label>Nombre de usuario</label>
              <input
                type="text"
                placeholder="Nombre"
                value={username}
                onChange={e => setUsername(e.target.value)}
                disabled={working}
              />
            </div>

            <div className="form-row">
              <label>Email</label>
              <input
                type="email"
                placeholder="email@dominio.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                disabled={working}
              />
            </div>

            <div className="form-row">
              <label>Contraseña</label>
              <div className="input-with-icon">
                <input
                  type={showRegPwd ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  disabled={working}
                />
                <button
                  type="button"
                  className="btn-min"
                  onClick={() => setShowRegPwd(s => !s)}
                  disabled={working}
                  aria-label={showRegPwd ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  title={showRegPwd ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                >
                  {showRegPwd ? 'Ocultar' : 'Mostrar'}
                </button>
              </div>
            </div>

            <div className="form-row">
              <label>Rol</label>
              <select value={role} onChange={e => setRole(e.target.value)} disabled={working}>
                <option value="admin">Admin</option>
                <option value="viewer">Viewer</option>
              </select>
            </div>

            {role === 'viewer' && (
              <div className="form-row">
                <label>Sucursal asignada</label>
                <select
                  value={sucursalId}
                  onChange={(e)=> setSucursalId(e.target.value)}
                  disabled={working}
                >
                  <option value="">Selecciona sucursal</option>
                  {sucursales.map(s => (
                    <option key={s.id} value={s.id}>
                      {s.nombre}{s.ubicacion ? ` – ${s.ubicacion}` : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={working}>
                {working ? 'Registrando…' : 'Registrar'}
              </button>
            </div>
          </form>
        </section>

        {/* Panel lista */}
        <section className="card">
          <h3 className="card-title">Listado</h3>

          {permError && (
            <div className="alert alert-warning small" role="alert">
              {permError}
            </div>
          )}

          <div className="acciones-usuario">
            <button className="btn-min" onClick={handleEdit} disabled={!selectedId || working}>Editar</button>
            <button className="btn-min" onClick={handleToggleDisabled} disabled={!selectedId || working}>
              Activar/Desactivar
            </button>
            <button className="btn-min danger" onClick={handleDelete} disabled={!selectedId || working}>
              Eliminar
            </button>
          </div>

          <div className="tabla-wrap">
            <table className="tabla">
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Email</th>
                  <th>Rol</th>
                  <th>Sucursal</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={5} className="empty">Cargando…</td></tr>
                ) : usuarios.length ? (
                  usuarios.map(u => (
                    <tr
                      key={u.id}
                      className={u.id === selectedId ? 'selected' : ''}
                      onClick={() => handleSelect(u.id)}
                    >
                      <td>{u.username || '—'}</td>
                      <td>{u.email || '—'}</td>
                      <td>{u.role || '—'}</td>
                      <td>{u.role === 'admin' ? 'Todas' : (sucMap[u.sucursalId] || '—')}</td>
                      <td>{u.disabled ? 'Deshabilitado' : 'Activo'}</td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan={5} className="empty">Sin usuarios</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
}
